import { useState } from "react";
import {
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { format, parseISO } from "date-fns";
import { CollectPermission, CollectUserRole, CollectUserView } from "generated/graphql";
import theme, { LinkSX } from "theme";
import { P } from "theme/typography";
import { selectFirmsWithLenses } from "redux/slices/client";
import { useAppSelector } from "redux/hooks";
import CollectUserLensPicker from "./CollectUserLensPicker";
import CollectUserPermissionPicker from "./CollectUserPermissionPicker";
import CollectUserRoleSelector from "./CollectUserRoleSelector";
import { CollectUserAccessCell } from "routes/users/collect/components/CollectUserAccessCell";
import { selectLenses } from "redux/slices/client";
import CollectUserFirmPicker from "./CollectUserFirmPicker";
import PSelect from "components/PSelect";
import { fullNameFromParts } from "helpers/name-helpers";
import { parseSafeInt } from "Backend";
import { ALL_COLLECT_PERMISSIONS } from "./ALL_COLLECT_PERMISSIONS";

export interface CollectUsersTableProps {
  users: CollectUserView[];
}

const CollectUsersTable = (props: CollectUsersTableProps) => {
  const selectableRowStyles = {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  };

  const { users } = props;
  const firms = useAppSelector(selectFirmsWithLenses);
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));
  const [currentSelectedUser, setCurrentSelectedUser] = useState<CollectUserView | undefined>();
  const [roleSelectorOpen, setRoleSelectorOpen] = useState(false);
  const [lensPickerOpen, setLensPickerOpen] = useState(false);
  const [firmPickerOpen, setFirmPickerOpen] = useState(false);
  const [permissionsPickerOpen, setPermissionsPickerOpen] = useState(false);
  const lenses = useAppSelector(selectLenses);
  const [filterSearch, setFilterSearch] = useState("");
  const [filterPermissions, setFilterPermissions] = useState<CollectPermission | undefined>(undefined);
  const [filterRole, setFilterRole] = useState<CollectUserRole | undefined>(undefined);
  const [filterFirm, setFilterFirm] = useState<number | undefined>(undefined);
  const [filterLens, setFilterLens] = useState<number | undefined>(undefined);
  const filteredUsers = users.filter((user) => {
    const search = filterSearch.toLocaleLowerCase();
    const searchMet =
      user.email.toLocaleLowerCase().indexOf(search) > -1 ||
      fullNameFromParts(user.firstName, user.lastName).toLocaleLowerCase().indexOf(search) > -1;
    if (filterRole !== undefined && user.role !== filterRole) {
      return false;
    }
    if (!searchMet) {
      return false;
    }
    if (filterPermissions !== undefined && !user.permissions.includes(filterPermissions)) {
      return false;
    }
    if (filterFirm !== undefined && !user.firms.map((f) => f.id).includes(filterFirm)) {
      return false;
    }
    if (filterLens !== undefined && !user.lensIds.includes(filterLens)) {
      return false;
    }
    return true;
  });

  const lensItems = (user: CollectUserView) => lenses.filter((l) => user.lensIds.includes(l.id));
  const firmItems = (user: CollectUserView) => firms.filter((f) => user.firms.find((firm) => firm.id === f.id));

  return (
    <Card className="mt-4 flex-1 overflow-auto">
      {users.length === 0 ? (
        <div className="flex justify-center items-center h-full">
          <p>No users found</p>
        </div>
      ) : (
        <CardContent className="flex flex-col h-full">
          <div className="flex gap-2 items-center">
            <FormControl size="small" sx={{ width: 250 }}>
              <TextField
                value={filterSearch}
                onChange={(evt) => setFilterSearch(evt.target.value || "")}
                label="Search"
                placeholder="Search name or email"
              />
            </FormControl>
            <FormControl size="small" sx={{ width: 150 }}>
              <InputLabel variant="outlined">Role</InputLabel>
              <PSelect
                data-pendo="collect-user-table-filter-role"
                label="Role"
                value={filterRole || ""}
                onChange={(evt) => {
                  const v = evt.target.value as CollectUserRole;
                  setFilterRole(v || undefined);
                }}>
                <MenuItem value="">Show All</MenuItem>
                <MenuItem value={CollectUserRole.Admin}>Admin</MenuItem>
                <MenuItem value={CollectUserRole.Contributor}>Contributor</MenuItem>
                <MenuItem value={CollectUserRole.Viewer}>Viewer</MenuItem>
              </PSelect>
            </FormControl>
            <FormControl size="small" sx={{ width: 250 }}>
              <InputLabel variant="outlined">Lens</InputLabel>
              <PSelect
                data-pendo="collect-user-table-filter-lens"
                label="Lens"
                value={filterLens?.toString() ?? ""}
                onChange={(evt) => {
                  const v = evt.target.value as string;
                  setFilterLens(parseSafeInt(v));
                }}>
                <MenuItem value="">Show All</MenuItem>
                {lenses.map((lens) => (
                  <MenuItem key={lens.id} value={lens.id.toString()}>
                    {lens.name}
                  </MenuItem>
                ))}
              </PSelect>
            </FormControl>
            <FormControl size="small" sx={{ width: 250 }}>
              <InputLabel variant="outlined">Firm</InputLabel>
              <PSelect
                data-pendo="collect-user-table-filter-firm"
                label="Firm"
                value={filterFirm?.toString() ?? ""}
                onChange={(evt) => {
                  const v = evt.target.value as string;
                  setFilterFirm(parseSafeInt(v));
                }}>
                <MenuItem value="">Show All</MenuItem>
                {firms.map((firm) => (
                  <MenuItem key={firm.id} value={firm.id.toString()}>
                    {firm.name}
                  </MenuItem>
                ))}
              </PSelect>
            </FormControl>
            <FormControl size="small" sx={{ width: 150 }}>
              <InputLabel variant="outlined">Permissions</InputLabel>
              <PSelect
                data-pendo="collect-user-table-filter-firm"
                label="Permissions"
                value={filterPermissions ?? ""}
                onChange={(evt) => {
                  const v = evt.target.value as string;
                  if (v === "") setFilterPermissions(undefined);
                  else setFilterPermissions(v as CollectPermission);
                }}>
                <MenuItem value="">Show All</MenuItem>
                <MenuItem value={CollectPermission.Exports}>Exports</MenuItem>
              </PSelect>
            </FormControl>
          </div>
          <TableContainer sx={{ height: "100%" }}>
            <Table stickyHeader sx={{ tableLayout: { xs: "fixed" } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  {!isMobile && (
                    <>
                      <TableCell>Join Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Lenses</TableCell>
                      <TableCell>Permissions</TableCell>
                      <TableCell>Firm</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user) => {
                  return (
                    <TableRow hover key={user.email} selected={false}>
                      <TableCell>
                        <P>{`${user.firstName ?? ""} ${user.lastName ?? ""}`}</P>
                      </TableCell>
                      <TableCell
                        title={user.email}
                        sx={{
                          overflow: { xs: "hidden" },
                          textOverflow: { xs: "ellipsis" },
                        }}>
                        {user.email}
                      </TableCell>
                      {!isMobile && (
                        <>
                          <TableCell>{format(parseISO(user.createdAt), "MM/dd/yyyy")}</TableCell>
                          <TableCell>{user.deactivated ? "Deactivated" : "Active"}</TableCell>
                          <TableCell
                            onClick={() => {
                              setCurrentSelectedUser(user);
                              setRoleSelectorOpen(true);
                            }}>
                            <P sx={LinkSX}>{user.role}</P>
                          </TableCell>
                          <TableCell>
                            <CollectUserAccessCell
                              accessType="Lenses"
                              items={lensItems(user)}
                              onEdit={() => {
                                setCurrentSelectedUser(user);
                                setLensPickerOpen(true);
                              }}
                            />
                          </TableCell>
                          <TableCell
                            sx={selectableRowStyles}
                            onClick={() => {
                              setCurrentSelectedUser(user);
                              setPermissionsPickerOpen(true);
                            }}>
                            <P sx={LinkSX}>
                              {user.permissions.length > 0
                                ? user.permissions
                                    .map(
                                      (permission) =>
                                        ALL_COLLECT_PERMISSIONS.find((p) => p.value === permission)?.name ?? ""
                                    )
                                    .join(", ")
                                : "No Permissions"}
                            </P>
                          </TableCell>

                          <TableCell>
                            <CollectUserAccessCell
                              accessType="Firms"
                              items={firmItems(user)}
                              onEdit={() => {
                                setCurrentSelectedUser(user);
                                setFirmPickerOpen(true);
                              }}
                            />
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      )}
      {currentSelectedUser && permissionsPickerOpen && (
        <CollectUserPermissionPicker
          open={permissionsPickerOpen}
          onClose={() => setPermissionsPickerOpen(false)}
          user={currentSelectedUser}
        />
      )}
      {currentSelectedUser && lensPickerOpen && (
        <CollectUserLensPicker
          open={lensPickerOpen}
          onClose={() => setLensPickerOpen(false)}
          user={currentSelectedUser}
        />
      )}
      {currentSelectedUser && roleSelectorOpen && (
        <CollectUserRoleSelector
          open={roleSelectorOpen}
          onClose={() => setRoleSelectorOpen(false)}
          user={currentSelectedUser}
        />
      )}
      {currentSelectedUser && firmPickerOpen && (
        <CollectUserFirmPicker
          open={firmPickerOpen}
          onClose={() => setFirmPickerOpen(false)}
          user={currentSelectedUser}
          firms={firms}
        />
      )}
    </Card>
  );
};

export default CollectUsersTable;
